export interface AdditionalBillingService {
  service_id: string;
  name: string;
  price: number;
  isActive?: boolean;
}

export interface AdditionalBillingVisitType {
  type_id: string;
  name?: string;
  name_ar?: string;
  normal_price?: number;
  is_active?: boolean;
}

export type AdditionalBillingSource = 'service' | 'visit_type';

export interface AdditionalBillingCatalogItem {
  selection_key: string;
  source_kind: AdditionalBillingSource;
  source_id: string;
  service_id: string;
  service_name: string;
  price: number;
}

const safePrice = (value: unknown): number => {
  const price = Number(value);
  return Number.isFinite(price) && price >= 0 ? price : 0;
};

/**
 * Visit types are offered as additional bill lines, never as a visit update.
 * Prefixing their billing IDs keeps them distinct from real service IDs while
 * allowing the existing billing/revenue pipeline to treat every choice as a
 * normal service item.
 */
export function buildAdditionalBillingCatalog(
  services: AdditionalBillingService[],
  visitTypes: AdditionalBillingVisitType[],
): AdditionalBillingCatalogItem[] {
  const serviceItems = (Array.isArray(services) ? services : [])
    .filter((service) => service?.isActive !== false)
    .map((service) => ({
      selection_key: `service:${service.service_id}`,
      source_kind: 'service' as const,
      source_id: service.service_id,
      service_id: service.service_id,
      service_name: service.name,
      price: safePrice(service.price),
    }));

  const visitTypeItems = (Array.isArray(visitTypes) ? visitTypes : [])
    .filter((visitType) => visitType?.is_active !== false)
    .map((visitType) => ({
      selection_key: `visit_type:${visitType.type_id}`,
      source_kind: 'visit_type' as const,
      source_id: visitType.type_id,
      service_id: `visit_type:${visitType.type_id}`,
      service_name: visitType.name_ar || visitType.name || visitType.type_id,
      price: safePrice(visitType.normal_price),
    }));

  return [...serviceItems, ...visitTypeItems];
}
